import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { colors, wardCategories, initialWardData, availableEquipment } from './BedAllotment/constants';
import { calculateDuration, getBedStyle, getBadgeStyle } from './BedAllotment/helpers';
import { 
  BedIcon, 
  ClipboardIcon, 
  GridIcon, 
  ListIcon, 
  SearchIcon, 
  WrenchIcon, 
  XIcon, 
  CheckIcon 
} from './BedAllotment/Icons';
import WardStats from './BedAllotment/WardStats';
import BedGrid from './BedAllotment/BedGrid';
import BedList from './BedAllotment/BedList';
import SidePanel from './BedAllotment/SidePanel';
import AdmissionFormModal from './BedAllotment/AdmissionFormModal';
import DischargeModal from './BedAllotment/DischargeModal';
import EditBedModal from './BedAllotment/EditBedModal';
import BedManagementModal from './BedAllotment/BedManagementModal';
import BedHistoryModal from './BedAllotment/BedHistoryModal';
import ConfirmDialog from './BedAllotment/ConfirmDialog';

const BedAllotment = () => {
  // State Management
  const [selectedWard, setSelectedWard] = useState('general_ward_a');
  const [selectedBeds, setSelectedBeds] = useState([]);
  const [hoveredBed, setHoveredBed] = useState(null);
  const [showAdmissionForm, setShowAdmissionForm] = useState(false);
  const [showDischargeModal, setShowDischargeModal] = useState(false);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [showEditBedModal, setShowEditBedModal] = useState(false);
  const [showBedManagementModal, setShowBedManagementModal] = useState(false);
  const [showBedHistory, setShowBedHistory] = useState(false);
  const [confirmAction, setConfirmAction] = useState(null);
  const [notification, setNotification] = useState({ show: false, text: '', type: 'success' });
  const [beds, setBeds] = useState([]);
  const [bedHistory, setBedHistory] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedBedForDischarge, setSelectedBedForDischarge] = useState(null);
  const [selectedBedForEdit, setSelectedBedForEdit] = useState(null);
  const [viewMode, setViewMode] = useState('grid');
  const [wards, setWards] = useState({});
  const [allWards, setAllWards] = useState([]);
  const [bedManagementMode, setBedManagementMode] = useState('overview');

  
  
  const [newBedData, setNewBedData] = useState({
    id: '',
    type: 'normal',
    floor: '1',
    equipment: [],
    notes: '',
    status: 'available'
  });

  const [newWardData, setNewWardData] = useState({
    name: '',
    type: '',
    department: '',
    floor: '',
    capacity: '',
    dailyFare: '',
    depositAmount: '',
    facilities: [],
    description: ''
  });

  const [formData, setFormData] = useState({
    patientName: '',
    age: '',
    gender: '',
    contact: '',
    emergencyContact: '',
    doctor: '',
    admissionDate: '',
    medicalCondition: '',
    bloodGroup: '',
    allergies: '',
    insuranceNumber: ''
  });

  const [editBedData, setEditBedData] = useState({
    id: '',
    status: 'available',
    type: 'normal',
    floor: '1',
    equipment: [],
    notes: '',
    maintenanceSchedule: ''
  });

  // Initialize Ward on Mount
  useEffect(() => {
    setWards(initialWardData);
    
    // Initialize allWards array from initialWardData
    const wardsArray = Object.keys(initialWardData).map(key => ({
      id: key,
      name: initialWardData[key].name,
      type: initialWardData[key].type,
      floor: initialWardData[key].floor,
      dailyFare: wardCategories[initialWardData[key].type]?.price || 2500,
      depositAmount: wardCategories[initialWardData[key].type]?.deposit || 10000,
      capacity: initialWardData[key].layout.flat().length,
      department: 'General Medicine',
      facilities: [],
      description: '',
      isPricingLocked: true
    }));
    setAllWards(wardsArray);
    
    initializeWard('general_ward_a');
  }, []);

  const initializeWard = useCallback((wardId) => {
    const ward = wards[wardId] || initialWardData[wardId];
    
    if (!ward) {
      // If ward not found in either location, try allWards
      const wardFromAll = allWards.find(w => w.id === wardId);
      if (wardFromAll && wardFromAll.layout) {
        const bedList = [];
        wardFromAll.layout.forEach(row => {
          row.forEach(bedId => {
            const initialBed = wardFromAll.initialState?.[bedId] || {};
            bedList.push({
              id: bedId,
              status: initialBed.status || 'available',
              patient: initialBed.patient || null,
              doctor: initialBed.doctor || null,
              admissionDate: initialBed.admissionDate || null,
              contact: initialBed.contact || null,
              wardType: wardFromAll.name,
              type: initialBed.type || wardFromAll.type,
              floor: initialBed.floor || wardFromAll.floor,
              equipment: initialBed.equipment || [],
              notes: initialBed.notes || '',
              maintenanceSchedule: initialBed.maintenanceSchedule || '',
              wardId: wardId
            });
          });
        });
        setBeds(bedList);
        setSelectedBeds([]);
        setSearchTerm('');
      }
      return;
    }
    
    const bedList = [];
    
    ward.layout.forEach(row => {
      row.forEach(bedId => {
        const initialBed = ward.initialState[bedId] || {};
        bedList.push({
          id: bedId,
          status: initialBed.status || 'available',
          patient: initialBed.patient || null,
          doctor: initialBed.doctor || null,
          admissionDate: initialBed.admissionDate || null,
          contact: initialBed.contact || null,
          wardType: wardCategories[ward.type]?.name || ward.name,
          type: initialBed.type || ward.type,
          floor: initialBed.floor || ward.floor,
          equipment: initialBed.equipment || [],
          notes: initialBed.notes || '',
          maintenanceSchedule: initialBed.maintenanceSchedule || '',
          wardId: wardId
        });
      });
    });
    
    setBeds(bedList);
    setSelectedBeds([]);
    setSearchTerm('');
  }, [wards, allWards]);

  // Handler to toggle ward facilities
  const handleWardFacilityToggle = (facility) => {
    setNewWardData(prev => ({
      ...prev,
      facilities: prev.facilities?.includes(facility)
        ? prev.facilities.filter(f => f !== facility)
        : [...(prev.facilities || []), facility]
    }));
  };

  // Handler to create new ward with automatic bed grid generation
  const handleCreateWard = (e) => {
    e.preventDefault();
    
    // Validate required fields
    if (!newWardData.name || !newWardData.type || !newWardData.department || 
        !newWardData.floor || !newWardData.capacity || 
        !newWardData.dailyFare || !newWardData.depositAmount) {
      showNotification('Please fill all required fields', 'error');
      return;
    }

    const wardId = `ward_${Date.now()}`;
    const capacity = parseInt(newWardData.capacity);
    
    // Generate bed layout automatically based on capacity (5 beds per row)
    const bedsPerRow = 5;
    const layout = [];
    const initialState = {};
    
    for (let i = 0; i < capacity; i++) {
      const rowIndex = Math.floor(i / bedsPerRow);
      const bedNumber = i + 1;
      // Use first letter of ward name for bed IDs (e.g., "Apollo Ward" -> A1, A2, A3...)
      const bedId = `${newWardData.name.charAt(0).toUpperCase()}${bedNumber}`;
      
      // Create row if it doesn't exist
      if (!layout[rowIndex]) {
        layout[rowIndex] = [];
      }
      
      // Add bed to layout
      layout[rowIndex].push(bedId);
      
      // Initialize bed state
      initialState[bedId] = {
        status: 'available',
        type: newWardData.type,
        floor: newWardData.floor,
        equipment: [],
        notes: ''
      };
    }

    // Create new ward object
    const newWard = {
      id: wardId,
      name: newWardData.name,
      type: newWardData.type,
      department: newWardData.department,
      floor: newWardData.floor,
      capacity: capacity,
      dailyFare: parseFloat(newWardData.dailyFare),
      depositAmount: parseFloat(newWardData.depositAmount),
      facilities: newWardData.facilities || [],
      description: newWardData.description || '',
      createdAt: new Date().toISOString(),
      isPricingLocked: true,
      layout: layout,
      initialState: initialState
    };

    // Add ward category if it doesn't exist
    if (!wardCategories[newWardData.type]) {
      wardCategories[newWardData.type] = {
        name: newWardData.name,
        color: colors.teal500,
        price: parseFloat(newWardData.dailyFare),
        nursing: 800,
        equipment: 500,
        deposit: parseFloat(newWardData.depositAmount)
      };
    }

    // Add to wards state
    setWards(prev => ({
      ...prev,
      [wardId]: newWard
    }));
    
    setAllWards(prev => [...prev, newWard]);

    // Automatically initialize beds for the new ward
    const bedList = [];
    layout.forEach(row => {
      row.forEach(bedId => {
        bedList.push({
          id: bedId,
          status: 'available',
          patient: null,
          doctor: null,
          admissionDate: null,
          contact: null,
          wardType: newWardData.name,
          type: newWardData.type,
          floor: newWardData.floor,
          equipment: [],
          notes: '',
          maintenanceSchedule: '',
          wardId: wardId
        });
      });
    });

    // Reset form
    setNewWardData({
      name: '',
      type: '',
      department: '',
      floor: '',
      capacity: '',
      dailyFare: '',
      depositAmount: '',
      facilities: [],
      description: ''
    });

    showNotification(`Ward "${newWard.name}" created with ${capacity} beds in grid layout!`, 'success');
    setBedManagementMode('overview');
    setShowBedManagementModal(false);
    
    // Switch to the new ward to see the beds immediately
    setSelectedWard(wardId);
    setBeds(bedList);
    setSelectedBeds([]);
  };

  // Event Handlers
  const handleBedRightClick = (e, bed) => {
    e.preventDefault();
    setSelectedBedForEdit(bed);
    setEditBedData({
      id: bed.id,
      status: bed.status,
      type: bed.type,
      floor: bed.floor,
      equipment: bed.equipment || [],
      notes: bed.notes || '',
      maintenanceSchedule: bed.maintenanceSchedule || ''
    });
    setShowEditBedModal(true);
  };

  const handleBedClick = useCallback((bed) => {
    if (bed.status === 'maintenance') {
      return;
    }

    if (bed.status === 'occupied') {
      setSelectedBedForDischarge(bed);
      setShowDischargeModal(true);
      return;
    }

    const isSelected = selectedBeds.some(b => b.id === bed.id);
    
    if (isSelected) {
      setSelectedBeds(selectedBeds.filter(b => b.id !== bed.id));
    } else {
      setSelectedBeds([...selectedBeds, bed]);
    }
  }, [selectedBeds]);

  const handleEditBedSubmit = (e) => {
    e.preventDefault();
    
    const updatedBeds = beds.map(bed => {
      if (bed.id === editBedData.id) {
        return {
          ...bed,
          status: editBedData.status,
          type: editBedData.type,
          floor: editBedData.floor,
          equipment: editBedData.equipment,
          notes: editBedData.notes,
          maintenanceSchedule: editBedData.maintenanceSchedule
        };
      }
      return bed;
    });
    
    setBeds(updatedBeds);
    setShowEditBedModal(false);
    showNotification(`Bed ${editBedData.id} updated successfully`, 'success');
  };

  const handleAllocateBeds = () => {
    if (selectedBeds.length === 0) return;
    setConfirmAction({ type: 'allocate', message: `Allocate ${selectedBeds.length} bed(s) for patient admission?` });
    setShowConfirmDialog(true);
  };

  const handleReserveBeds = () => {
    if (selectedBeds.length === 0) return;
    setConfirmAction({ type: 'reserve', message: `Reserve ${selectedBeds.length} bed(s)?` });
    setShowConfirmDialog(true);
  };

  const executeReservation = () => {
    const updatedBeds = beds.map(bed => {
      if (selectedBeds.some(sb => sb.id === bed.id)) {
        return { ...bed, status: 'reserved' };
      }
      return bed;
    });
    
    setBeds(updatedBeds);
    setSelectedBeds([]);
    showNotification(`${selectedBeds.length} bed(s) reserved successfully`, 'success');
    setShowConfirmDialog(false);
  };

  const handleClearSelection = () => {
    setSelectedBeds([]);
    showNotification('Selection cleared', 'info');
  };

  const handleDischargeBed = () => {
    if (!selectedBedForDischarge) return;
    
    const updatedBeds = beds.map(bed => {
      if (bed.id === selectedBedForDischarge.id) {
        const historyEntry = {
          bedId: bed.id,
          patient: bed.patient,
          doctor: bed.doctor,
          admissionDate: bed.admissionDate,
          dischargeDate: new Date().toISOString().split('T')[0],
          duration: calculateDuration(bed.admissionDate)
        };
        setBedHistory([historyEntry, ...bedHistory]);
        
        return { 
          ...bed, 
          status: 'available', 
          patient: null, 
          doctor: null, 
          admissionDate: null,
          contact: null
        };
      }
      return bed;
    });
    
    setBeds(updatedBeds);
    showNotification(`Patient ${selectedBedForDischarge.patient} discharged from bed ${selectedBedForDischarge.id}`, 'success');
    setShowDischargeModal(false);
    setSelectedBedForDischarge(null);
  };

  const handleFormSubmit = (e) => {
    e.preventDefault();
    
    const updatedBeds = beds.map(bed => {
      if (selectedBeds.some(sb => sb.id === bed.id)) {
        return {
          ...bed,
          status: 'occupied',
          patient: formData.patientName,
          doctor: formData.doctor,
          admissionDate: formData.admissionDate,
          contact: formData.contact
        };
      }
      return bed;
    });
    
    setBeds(updatedBeds);
    setShowAdmissionForm(false);
    setSelectedBeds([]);
    setFormData({
      patientName: '',
      age: '',
      gender: '',
      contact: '',
      emergencyContact: '',
      doctor: '',
      admissionDate: '',
      medicalCondition: '',
      bloodGroup: '',
      allergies: '',
      insuranceNumber: ''
    });
    
    showNotification(`Patient ${formData.patientName} admitted successfully to ${selectedBeds.map(b => b.id).join(', ')}`, 'success');
    setShowConfirmDialog(false);
  };

  const handleFormChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleEditBedDataChange = (e) => {
    const { name, value } = e.target;
    setEditBedData({
      ...editBedData,
      [name]: value
    });
  };

  const handleEquipmentToggle = (equipment) => {
    const currentEquipment = editBedData.equipment || [];
    if (currentEquipment.includes(equipment)) {
      setEditBedData({
        ...editBedData,
        equipment: currentEquipment.filter(e => e !== equipment)
      });
    } else {
      setEditBedData({
        ...editBedData,
        equipment: [...currentEquipment, equipment]
      });
    }
  };

  // const handleAddNewBed = (e) => {
  //   e.preventDefault();
    
  //   if (beds.some(bed => bed.id === newBedData.id)) {
  //     showNotification(`Bed ID ${newBedData.id} already exists!`, 'error');
  //     return;
  //   }

  //   const newBed = {
  //     ...newBedData,
  //     patient: null,
  //     doctor: null,
  //     admissionDate: null,
  //     contact: null,
  //     wardType: wardCategories[newBedData.type]?.name || 'General Ward',
  //     wardId: selectedWard
  //   };

  //   setBeds([...beds, newBed]);
  //   showNotification(`Bed ${newBedData.id} added successfully`, 'success');
    
  //   setNewBedData({
  //     id: '',
  //     type: 'normal',
  //     floor: '1',
  //     equipment: [],
  //     notes: '',
  //     status: 'available'
  //   });
    
  //   setBedManagementMode('overview');
  // };
  const handleAddNewBed = (e) => {
  e.preventDefault();

  if (beds.some(bed => bed.id === newBedData.id)) {
    showNotification(`Bed ID ${newBedData.id} already exists!`, 'error');
    return;
  }

  const newBed = {
    ...newBedData,
    patient: null,
    doctor: null,
    admissionDate: null,
    contact: null,
    wardType: wardCategories[newBedData.type]?.name || 'General Ward',
    wardId: selectedWard
  };

  // 1) add to beds state
  setBeds(prev => [...prev, newBed]);

  // 2) ensure the current ward layout contains this bed id so BedGrid will render it
  setWards(prev => {
    const prevWard = prev[selectedWard] || initialWardData[selectedWard];
    if (!prevWard) {
      // If ward not found in state, create a basic ward entry with a fallback layout
      return {
        ...prev,
        [selectedWard]: {
          ...(initialWardData[selectedWard] || {}),
          layout: [[newBed.id]],
          initialState: { ...(initialWardData[selectedWard]?.initialState || {}), [newBed.id]: { status: newBed.status, type: newBed.type, floor: newBed.floor } }
        }
      };
    }

    // clone layout and add the bed id to the last row (or create a row)
    const updatedLayout = Array.isArray(prevWard.layout) ? prevWard.layout.map(r => [...r]) : [];
    if (updatedLayout.length === 0) {
      updatedLayout.push([newBed.id]);
    } else {
      // try to add to last row
      updatedLayout[updatedLayout.length - 1].push(newBed.id);
    }

    // update initialState as well (optional but useful)
    const updatedInitialState = { ...(prevWard.initialState || {}) };
    updatedInitialState[newBed.id] = {
      status: newBed.status || 'available',
      type: newBed.type,
      floor: newBed.floor,
      equipment: newBed.equipment || [],
      notes: newBed.notes || ''
    };

    return {
      ...prev,
      [selectedWard]: {
        ...prevWard,
        layout: updatedLayout,
        initialState: updatedInitialState
      }
    };
  });

  showNotification(`Bed ${newBedData.id} added successfully`, 'success');

  // reset newBed form
  setNewBedData({
    id: '',
    type: 'normal',
    floor: '1',
    equipment: [],
    notes: '',
    status: 'available'
  });

  setBedManagementMode('overview');
};


  const handleRemoveBed = (bedId) => {
    const bedToRemove = beds.find(b => b.id === bedId);
    
    if (bedToRemove && bedToRemove.status === 'occupied') {
      showNotification('Cannot remove occupied bed! Discharge patient first.', 'error');
      return;
    }

    setBeds(beds.filter(bed => bed.id !== bedId));
    showNotification(`Bed ${bedId} removed successfully`, 'success');
  };

  const handleNewBedEquipmentToggle = (equipment) => {
    const currentEquipment = newBedData.equipment || [];
    if (currentEquipment.includes(equipment)) {
      setNewBedData({
        ...newBedData,
        equipment: currentEquipment.filter(e => e !== equipment)
      });
    } else {
      setNewBedData({
        ...newBedData,
        equipment: [...currentEquipment, equipment]
      });
    }
  };

  const showNotification = (text, type = 'success') => {
    setNotification({ show: true, text, type });
    setTimeout(() => {
      setNotification({ show: false, text: '', type: 'success' });
    }, 3000);
  };

  const confirmActionExecute = () => {
    if (confirmAction?.type === 'allocate') {
      setShowAdmissionForm(true);
      setShowConfirmDialog(false);
    } else if (confirmAction?.type === 'reserve') {
      executeReservation();
    }
  };

  const getNotificationStyle = (type) => {
    const baseStyle = {
      position: 'fixed',
      bottom: '24px',
      right: '24px',
      padding: '16px 24px',
      borderRadius: '8px',
      fontSize: '14px',
      boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
      zIndex: 2000,
      display: 'flex',
      alignItems: 'center',
      gap: '12px',
      minWidth: '300px'
    };
    
    if (type === 'success') {
      return { ...baseStyle, background: colors.green500, color: colors.cream50 };
    } else if (type === 'error') {
      return { ...baseStyle, background: colors.red500, color: colors.cream50 };
    } else if (type === 'info') {
      return { ...baseStyle, background: colors.slate900, color: colors.cream100 };
    }
    return baseStyle;
  };

  // Computed Values with null safety
  const getFilteredBeds = useMemo(() => {
    let filtered = beds;
    
    if (searchTerm) {
      filtered = filtered.filter(bed => 
        bed.id.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (bed.patient && bed.patient.toLowerCase().includes(searchTerm.toLowerCase())) ||
        (bed.doctor && bed.doctor.toLowerCase().includes(searchTerm.toLowerCase()))
      );
    }
    
    return filtered;
  }, [beds, searchTerm]);

  const currentWard = wards[selectedWard] || 
                      initialWardData[selectedWard] || 
                      allWards.find(w => w.id === selectedWard);
  
  const currentCategory = wardCategories[currentWard?.type] || {
    name: currentWard?.name || 'General Ward',
    color: colors.teal500,
    price: currentWard?.dailyFare ?? 2500,
    nursing: 800,
    equipment: 500,
    deposit: currentWard?.depositAmount ?? 10000
  };
  
  const stats = useMemo(() => ({
    total: beds.length,
    available: beds.filter(b => b.status === 'available').length,
    occupied: beds.filter(b => b.status === 'occupied').length,
    reserved: beds.filter(b => b.status === 'reserved').length,
    maintenance: beds.filter(b => b.status === 'maintenance').length,
    occupancyRate: beds.length > 0 ? Math.round((beds.filter(b => b.status === 'occupied').length / beds.length) * 100) : 0
  }), [beds]);

const displayBed = selectedBeds.length === 1 ? selectedBeds[0] : null;


  const totalDailyCharge = (currentCategory?.price ?? 0) + 
                           (currentCategory?.nursing ?? 0) + 
                           (currentCategory?.equipment ?? 0);
  const filteredBeds = getFilteredBeds;

  // Inline Styles
  const styles = {
    container: {
      maxWidth: '1600px',
      margin: '0 auto',
      padding: '24px',
      minHeight: '100vh',
      fontSize: '14px',
      lineHeight: '1.5',
      color: colors.slate900,
      background: colors.cream50
    },
    header: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      marginBottom: '32px',
      paddingBottom: '24px',
      borderBottom: `1px solid rgba(94, 82, 64, 0.2)`,
      flexWrap: 'wrap',
      gap: '16px'
    },
    h1: {
      fontSize: '24px',
      fontWeight: 550,
      letterSpacing: '-0.01em',
      color: colors.slate900,
      margin: '0 0 4px 0'
    },
    headerControls: {
      display: 'flex',
      gap: '12px',
      alignItems: 'center',
      flexWrap: 'wrap'
    },
    select: {
      padding: '10px 16px',
      fontSize: '14px',
      fontFamily: 'inherit',
      border: `1px solid rgba(94, 82, 64, 0.2)`,
      borderRadius: '8px',
      background: colors.cream100,
      color: colors.slate900,
      cursor: 'pointer',
      appearance: 'none',
      backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='16' height='16' viewBox='0 0 24 24' fill='none' stroke='%23134252' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpolyline points='6 9 12 15 18 9'%3E%3C/polyline%3E%3C/svg%3E")`,
      backgroundRepeat: 'no-repeat',
      backgroundPosition: 'right 12px center',
      paddingRight: '32px',
      minWidth: '200px'
    },
    searchInputWrapper: {
      position: 'relative',
      display: 'flex',
      alignItems: 'center'
    },
    searchIcon: {
      position: 'absolute',
      left: '12px',
      pointerEvents: 'none',
      color: colors.slate500
    },
    searchInput: {
      padding: '10px 16px 10px 40px',
      fontSize: '14px',
      fontFamily: 'inherit',
      border: `1px solid rgba(94, 82, 64, 0.2)`,
      borderRadius: '8px',
      background: colors.cream100,
      color: colors.slate900,
      minWidth: '250px',
      outline: 'none'
    },
    iconButton: {
      padding: '10px 16px',
      fontSize: '14px',
      fontFamily: 'inherit',
      border: `1px solid rgba(94, 82, 64, 0.2)`,
      borderRadius: '8px',
      background: colors.cream100,
      color: colors.slate900,
      cursor: 'pointer',
      display: 'flex',
      whiteSpace:"nowrap",
      alignItems: 'center',
      gap: '8px',
      transition: 'all 0.2s ease'
    },
    mainContent: {
      display: 'grid',
      gridTemplateColumns: '1fr 350px',
      gap: '24px',
      marginBottom: '32px'
    },
    bedArea: {
      background: colors.cream100,
      border: `1px solid rgba(94, 82, 64, 0.12)`,
      borderRadius: '12px',
      padding: '24px'
    },
    bedAreaHeader: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      flexWarp:"wrap",
      marginBottom: '20px'
    },
    legend: {
      display: 'flex',
      flexWrap: 'wrap',
      gap: '16px',
      marginBottom: '24px',
      paddingBottom: '16px',
      borderBottom: `1px solid rgba(94, 82, 64, 0.12)`
    },
    legendItem: {
      display: 'flex',
      alignItems: 'center',
      gap: '8px'
    },
    legendColor: {
      width: '20px',
      height: '20px',
      borderRadius: '6px',
      border: '1px solid rgba(0, 0, 0, 0.1)'
    },
    legendLabel: {
      fontSize: '12px',
      color: colors.slate500
    },
    infoBox: {
      padding: '12px',
      background: 'rgba(33, 128, 141, 0.08)',
      border: `1px solid rgba(33, 128, 141, 0.2)`,
      borderRadius: '8px',
      fontSize: '12px',
      color: colors.slate900,
      marginTop: '16px'
    }
  };

  return (
    <div style={styles.container}>
      <header style={styles.header}>
        <div>
          <h1 style={styles.h1}>Bed Allocation</h1>
          <p style={{ margin: 0, color: colors.slate500 }}>
            Visual bed management & patient admission
          </p>
        </div>
        
        <div style={styles.headerControls}>
          <div style={styles.searchInputWrapper}>
            <div style={styles.searchIcon}>
              <SearchIcon />
            </div>
            <input
              type="text"
              placeholder="Search bed, patient, doctor..."
              style={styles.searchInput}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
          <select 
            style={styles.select}
            value={selectedWard}
            onChange={(e) => {
              setSelectedWard(e.target.value);
              initializeWard(e.target.value);
            }}
          >
            <option value="general_ward_a">General Ward A</option>
            <option value="icu_ward">ICU Ward</option>
            <option value="premium_ward">Premium Ward</option>
            
            {allWards
              .filter(ward => !['general_ward_a', 'icu_ward', 'premium_ward'].includes(ward.id))
              .map(ward => (
                <option key={ward.id} value={ward.id}>
                  {ward.name}
                </option>
              ))}
          </select>
          <button 
            style={styles.iconButton}
            onClick={() => setShowBedManagementModal(true)}
          >
            <WrenchIcon />
            Bed Management
          </button>
          <button 
            style={styles.iconButton}
            onClick={() => setShowBedHistory(!showBedHistory)}
          >
            <ClipboardIcon />
            History
          </button>
        </div>
      </header>

      <WardStats stats={stats} currentCategory={currentCategory} />

      <main style={styles.mainContent}>
        <section style={styles.bedArea}>
          <div style={styles.bedAreaHeader}>
            <div style={styles.legend}>
              <div style={styles.legendItem}>
                <div style={{ ...styles.legendColor, background: colors.teal500 }}></div>
                <span style={styles.legendLabel}>Available</span>
              </div>
              <div style={styles.legendItem}>
                <div style={{ ...styles.legendColor, background: colors.red500 }}></div>
                <span style={styles.legendLabel}>Occupied</span>
              </div>
              <div style={styles.legendItem}>
                <div style={{ ...styles.legendColor, background: colors.orange500 }}></div>
                <span style={styles.legendLabel}>Selected</span>
              </div>
              <div style={styles.legendItem}>
                <div style={{ ...styles.legendColor, background: colors.gray300 }}></div>
                <span style={styles.legendLabel}>Maintenance</span>
              </div>
              <div style={styles.legendItem}>
                <div style={{ ...styles.legendColor, background: colors.orange400 }}></div>
                <span style={styles.legendLabel}>Reserved</span>
              </div>
            </div>
            <button 
              style={styles.iconButton}
              onClick={() => setViewMode(viewMode === 'grid' ? 'list' : 'grid')}
            >
              {viewMode === 'grid' ? (
                <>
                  <ListIcon />
                  List View
                </>
              ) : (
                <>
                  <GridIcon />
                  Grid View
                </>
              )}
            </button>
          </div>

          <div style={styles.infoBox}>
            <strong>Tip:</strong> Right-click on any bed to edit its configuration, equipment, and maintenance details.
          </div>

          {viewMode === 'grid' ? (
            <BedGrid 
              currentWard={currentWard}
              beds={beds}
              filteredBeds={filteredBeds}
              selectedBeds={selectedBeds}
              hoveredBed={hoveredBed}
              setHoveredBed={setHoveredBed}
              handleBedClick={handleBedClick}
              handleBedRightClick={handleBedRightClick}
            />
          ) : (
            <BedList 
              filteredBeds={filteredBeds}
              handleBedClick={handleBedClick}
              handleBedRightClick={handleBedRightClick}
              currentWard={currentWard}
            />
          )}
        </section>

        <SidePanel 
          selectedBeds={selectedBeds}
          displayBed={displayBed}
          currentCategory={currentCategory}
          totalDailyCharge={totalDailyCharge}
          handleAllocateBeds={handleAllocateBeds}
          handleReserveBeds={handleReserveBeds}
          handleClearSelection={handleClearSelection}
        />
      </main>

      {/* All Modals */}
      <AdmissionFormModal 
        show={showAdmissionForm}
        onClose={() => setShowAdmissionForm(false)}
        formData={formData}
        handleFormSubmit={handleFormSubmit}
        handleFormChange={handleFormChange}
      />

      <DischargeModal 
        show={showDischargeModal}
        onClose={() => setShowDischargeModal(false)}
        selectedBedForDischarge={selectedBedForDischarge}
        currentCategory={currentCategory}
        totalDailyCharge={totalDailyCharge}
        handleDischargeBed={handleDischargeBed}
      />

      <EditBedModal 
        show={showEditBedModal}
        onClose={() => setShowEditBedModal(false)}
        editBedData={editBedData}
        handleEditBedSubmit={handleEditBedSubmit}
        handleEditBedDataChange={handleEditBedDataChange}
        handleEquipmentToggle={handleEquipmentToggle}
      />

      <BedManagementModal 
        show={showBedManagementModal}
        onClose={() => setShowBedManagementModal(false)}
        bedManagementMode={bedManagementMode}
        setBedManagementMode={setBedManagementMode}
        currentWard={currentWard}
        stats={stats}
        beds={beds}
        setBeds={setBeds}
        newBedData={newBedData}
        setNewBedData={setNewBedData}
        handleAddNewBed={handleAddNewBed}
        handleRemoveBed={handleRemoveBed}
        handleNewBedEquipmentToggle={handleNewBedEquipmentToggle}
        setSelectedBedForEdit={setSelectedBedForEdit}
        setEditBedData={setEditBedData}
        setShowEditBedModal={setShowEditBedModal}
        showNotification={showNotification}
        allWards={allWards}
        newWardData={newWardData}
        setNewWardData={setNewWardData}
        handleCreateWard={handleCreateWard}
        handleWardFacilityToggle={handleWardFacilityToggle}
      />

      <BedHistoryModal 
        show={showBedHistory}
        onClose={() => setShowBedHistory(false)}
        bedHistory={bedHistory}
      />

      <ConfirmDialog 
        show={showConfirmDialog}
        onClose={() => setShowConfirmDialog(false)}
        confirmAction={confirmAction}
        confirmActionExecute={confirmActionExecute}
      />

      {/* Notification */}
      {notification.show && (
        <div style={getNotificationStyle(notification.type)}>
          {notification.type === 'success' && <CheckIcon />}
          {notification.type === 'info' && <ClipboardIcon />}
          {notification.type === 'error' && <XIcon />}
          <span>{notification.text}</span>
        </div>
      )}
    </div>
  );
};

export default BedAllotment;
